import { TextInfoGroupAlt } from "@/components/shared";
import {
  CarPurchasePriceDetails,
  CarDetails,
  CostDetails,
  PurchaseDetails,
  ServiceType,
  Shift,
  SiteVisit,
  Tender,
  TenderDurationFragment,
  StandalonePriceDetails,
} from "@/graphql/__generated__/graphql";
import AdvancedCostParametersSection from "./Sections/AdvancedCostParametersSection";
import ShiftsSection from "./Sections/ShiftsSection";
import SiteVisitsSection from "./Sections/SiteVisitsSection";
import { appendSiteAddress } from "@/utils/helpers";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import PricingSection from "./Sections/PricingSection";
import { cn } from "@/lib/utils";
import CarsSection from "./Sections/CarsSection";
import CostDetailsSection from "./Sections/CostDetailsSection";
import PurchasesSection from "./Sections/PurchasesSection";
import { CalculationOverviewSubject } from "../../types";
import StandalonePriceDetailsCard from "./Sections/StandalonePriceDetailsCard";
import TotalOverviewPricing from "./Sections/TotalOverviewPricing";
import TenderKpiSection from "./Sections/TenderKpiSection";

interface SidebarProps {
  subject: CalculationOverviewSubject | null;
  compareType: "selected" | "comparing";
  isComparing?: boolean;
  tenderData?: Tender;
  standaloneCars?: StandalonePriceDetails;
  standalonePurchases?: StandalonePriceDetails;
}

function Sidebar({ subject, compareType, isComparing = false, tenderData, standaloneCars, standalonePurchases }: SidebarProps) {
  const { t } = useTranslation();

  const outletContext = useOutletContext<EditTenderContext | undefined>();
  const tenderFromContext = outletContext?.tenderByIdData?.fetchTenderById;
  const tender = tenderData ?? tenderFromContext;
  const contract = tender?.contract;
  const startDate = contract?.startDate;
  const endDate = contract?.endDate;
  const durationMonths = contract?.durationMonths;

  if (!subject) return (
    <div className="flex flex-col gap-2 h-[calc(100vh-165px)] justify-center items-center mx-auto">
      <div className="text-sm text-slate-500 font-normal">{t("edittender.calculationoverview.common.noserviceselected")}</div>
    </div>
  );

  const tenderDuration: TenderDurationFragment = {
    contract: {
      startDate: startDate ?? "",
      endDate: endDate ?? ""
    }
  };

  return (
    <ScrollArea className="grow">
      <div className="flex flex-col gap-2 pr-4 pb-[300px]">
        {/* Header */}
        {
          isComparing && (
            <em className="text-xs text-slate-500 font-normal">{t(`edittender.calculationoverview.common.${compareType}`)}</em>
          )
        }
        {subject.kind === "service" ? (
          /* Service name and organization name */
          <div className="flex items-center gap-x-4 flex-wrap">
            <span className={cn("break-all text-lg font-semibold", compareType === "selected" ? "text-blue-500" : "text-orange-500")}>
              {subject.service.name}
            </span>
            <span className="text-sm text-slate-500 font-normal">{subject.service.operationalOrganizationShortName}</span>
            <div className="grow"></div>
            {/* Service type */}
            <Badge variant="outline" className="text-xs">{t(`common:servicetype.${subject.service.type.toLowerCase()}`)}</Badge>
          </div>
        ) : (
          /* Tender name */
          <div className="flex items-center gap-x-4 flex-wrap">
            <span className={cn("break-all text-lg font-semibold", compareType === "selected" ? "text-blue-500" : "text-orange-500")}>
              {subject.tender.name}
            </span>
            <div className="grow"></div>
            <Badge variant="default" className="text-xs">
              {t("common:chance")}
            </Badge>
          </div>
        )}

        {/* Site Details (Service only) */}
        {subject.kind === "service" && (
          <TextInfoGroupAlt
            label={t("edittender.calculationoverview.common.site")}
            content={`${subject.service.site?.name} - ${appendSiteAddress(subject.service.site)}`}
          />
        )}

        {/* Content Components */}
        <div className="flex flex-col gap-2">
          {subject.kind === "service" ? (
            <>
              <TotalOverviewPricing
                service={subject.service}
              />
              {/* Pricing */}
              <PricingSection service={subject.service} durationMonths={durationMonths ?? 0} />
              {/* Advanced cost parameters */}
              <AdvancedCostParametersSection
                acp={{
                  ...subject.service.advancedCostParameters,
                  carCostsPerMinute: subject.service.advancedCostParameters.carCostsPerMinute ?? undefined,
                  otherCostsPerMinute: subject.service.advancedCostParameters.otherCostsPerMinute ?? undefined
                }}
                organizationId={subject.service.operationalOrganizationId}
                serviceType={subject.service.type}
              />
              {/* Shifts */}
              {subject.service.type === ServiceType.Permanent && "shifts" in subject.service && (
                <ShiftsSection shifts={subject.service.shifts as Shift[]} tenderDuration={tenderDuration} />
              )}
              {/* Site visits */}
              {subject.service.type === ServiceType.Mobile && "siteVisits" in subject.service && (
                <SiteVisitsSection siteVisits={subject.service.siteVisits as SiteVisit[]} tenderDuration={tenderDuration} />
              )}
              {/* Cars and purchases (Permanent services only) */}
              {subject.service.type === ServiceType.Permanent && (
                <>
                  <CarsSection
                    cars={subject.service.cars as CarDetails[]}
                    carPurchasePriceDetails={subject.service.pricing.carPurchasePricing as CarPurchasePriceDetails[]}
                    serviceId={subject.service.id}
                  />
                  <PurchasesSection
                    purchases={subject.service.purchases as PurchaseDetails[]}
                    carPurchasePriceDetails={subject.service.pricing.carPurchasePricing as CarPurchasePriceDetails[]}
                    serviceId={subject.service.id}
                  />
                </>
              )}
              <CostDetailsSection costDetails={subject.service.costDetails as CostDetails[]} />
            </>
          ) : (
            <>
              <TenderKpiSection tenderPriceDetails={subject.tender} />
              {/* Use props if available, otherwise fall back to subject data for backward compatibility */}
              <StandalonePriceDetailsCard
                title={t("edittender.calculationoverview.common.standalonecars")}
                description={t("edittender.calculationoverview.common.standalonecarsdescription")}
                details={standaloneCars ?? subject.standaloneCars}
              />
              <StandalonePriceDetailsCard
                title={t("edittender.calculationoverview.common.standalonepurchases")}
                description={t("edittender.calculationoverview.common.standalonepurchasesdescription")}
                details={standalonePurchases ?? subject.standalonePurchases}
              />
              <CostDetailsSection costDetails={(subject.tenderCostDetails ?? []) as CostDetails[]} />
            </>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}

export default Sidebar;
